import { Style } from 'ol/style';

import {
  IMAGE_LOADING,
  IMAGE_LOADED,
  IMAGE_ERROR,
  DEFAULT_MARK_SIZE,
  DEFAULT_MARK_WELLKNOWNNAME
} from '../constants';
import { memoizeStyleFunction, memoizeSymbolizerInfoFunction } from './styleUtils';
import {
  imageLoadingPointStyle,
  imageErrorPointStyle,
  emptyStyle,
} from './static';
import { createCachedImageStyle, getImageLoadingState } from '../imageCache';
import getWellKnownSymbol from './wellknown';
import evaluate, { expressionOrDefault } from '../olEvaluator';
import { getSimpleFill, getSimpleStroke } from './simpleStyles';

const defaultMarkFill = getSimpleFill({ styling: { fill: '#888888' } });
const defaultMarkStroke = getSimpleStroke({ styling: { stroke: {} } });

/**
 * @private
 * @param  {PointSymbolizer} pointsymbolizer [description]
 * @return {object} openlayers style
 */
function pointStyle(pointsymbolizer) {
  const { graphic: style } = pointsymbolizer;

  // If the point size is a dynamic expression, use the default point size and update in-place later.
  let pointSizeValue = expressionOrDefault(style.size, DEFAULT_MARK_SIZE);

  // If the point rotation is a dynamic expression, use 0 as default rotation and update in-place later.
  const rotationDegrees = expressionOrDefault(style.rotation, 0.0);
  var displacementX = (style.displacement == null || style.displacement.displacementx == null || style.displacement.displacementx == '') ? 0 : Number(style.displacement.displacementx);
  var displacementY = (style.displacement == null || style.displacement.displacementy == null || style.displacement.displacementy == '') ? 0 : Number(style.displacement.displacementy);

  var opacity = (style.opacity === null || style.opacity === '') ? 1 : Number(style.opacity);

  const displacement = style.displacement == null ? [0, 0] : [displacementX, displacementY];

  var anchorX = (style.anchorpoint == null || style.anchorpoint.anchorpointx == null || style.anchorpoint.anchorpointx == '') ? 0 : Number(style.anchorpoint.anchorpointx);
  var anchorY = (style.anchorpoint == null || style.anchorpoint.anchorpointy == null || style.anchorpoint.anchorpointy == '') ? 0 : Number(style.anchorpoint.anchorpointy);

  const anchor = style.anchorpoint == null ? [0.5, 0.5] : [anchorX, 1 - anchorY];

  if (style.externalgraphic && style.externalgraphic.onlineresource) {
    // For external graphics: the default size is the native image size.
    // In that case, set pointSizeValue to null, so no scaling is calculated for the image.
    if (!style.size) {
      pointSizeValue = null;
    }

    const imageUrl = style.externalgraphic.onlineresource;

      // Use fallback point styles when image hasn't been loaded yet.
      switch (getImageLoadingState(imageUrl)) {
      case IMAGE_LOADED:
        return createCachedImageStyle(
          imageUrl,
          pointSizeValue,
          rotationDegrees,
          opacity,
          displacement,
          anchor
        );
      case IMAGE_LOADING:
        return imageLoadingPointStyle;
      case IMAGE_ERROR:
        return imageErrorPointStyle;
      default:
        // A symbolizer should have loading state metadata, but return IMAGE_LOADING just in case.
        return imageLoadingPointStyle;
    }
  }

  if (style.mark) {
    const { wellknownname } = style.mark;
    const olFill = getSimpleFill(style.mark.fill);
    const olStroke = getSimpleStroke(style.mark.stroke);

    return new Style({
      // Note: size will be set dynamically later.
      image: getWellKnownSymbol(
        wellknownname,
        pointSizeValue,
        olStroke,
        olFill,
        rotationDegrees,
        displacement
      ),
    });
  }

  // SLD spec: when no ExternalGraphic or Mark is specified,
  // use a square of 6 pixels with 50% gray fill and a black outline.
  return new Style({
    image: getWellKnownSymbol(
      DEFAULT_MARK_WELLKNOWNNAME,
      pointSizeValue,
      defaultMarkStroke,
      defaultMarkFill,
      rotationDegrees,
      displacement
    ),
  });
}

const cachedPointStyle = memoizeStyleFunction(pointStyle);
const cachedSymbolizerInfo = memoizeSymbolizerInfoFunction();

/**
 * @private
 * Get an OL point style instance for a feature according to a symbolizer.
 * @param {object} symbolizer SLD symbolizer object.
 * @param {ol/Feature} feature OpenLayers Feature.
 * @param {Function} getProperty A property getter: (feature, propertyName) => property value.
 * @returns {ol/Style} OpenLayers style instance.
 */
function getPointStyle(symbolizer, feature, getProperty) {
    // According to SLD spec, when a point symbolizer has no Graphic, nothing will be rendered.
    if (!(symbolizer && symbolizer.graphic)) {
        return emptyStyle;
    }

    const olStyle = cachedPointStyle(symbolizer);
    const symbolizerInfo = cachedSymbolizerInfo(symbolizer);

    if (feature.get('xstate') == null || feature.get('xstate') == 0) {
        olStyle.setZIndex(0);
    } else {
        olStyle.setZIndex(100);
    }

  // Reset previous calculated point geometry left by evaluating point style for a line or polygon feature.
  olStyle.setGeometry(null);

  let olImage = olStyle.getImage();

  // Apply dynamic values to the cached OL style instance before returning it.

  // --- Update dynamic size ---
  const { graphic } = symbolizer;
  const { size } = graphic;
  if (size && size.type === 'expression') {
    const sizeValue =
      Number(evaluate(size, feature, getProperty)) || DEFAULT_MARK_SIZE;

    if (graphic.externalgraphic && graphic.externalgraphic.onlineresource) {
      const height = olImage.getSize()[1];
      const scale = sizeValue / height || 1;
      olImage.setScale(scale);
    } else if (graphic.mark && graphic.mark.wellknownname === 'circle') {
      // Note: only ol/style/Circle has a setter for radius. RegularShape does not.
      olImage.setRadius(sizeValue * 0.5);
    } else {
      // For a non-Circle RegularShape, create a new olImage in order to update the size.
      olImage = getWellKnownSymbol(
        (graphic.mark && graphic.mark.wellknownname) || DEFAULT_MARK_WELLKNOWNNAME,
        sizeValue,
        // Note: re-use stroke and fill instances for a (small?) performance gain.
        olImage.getStroke(),
        olImage.getFill()
      );
      olStyle.setImage(olImage);
    }
  }

  // --- Update dynamic rotation ---
  const { rotation } = graphic;
  if (rotation && rotation.type === 'expression') {
    const rotationDegrees =
      Number(evaluate(rotation, feature, getProperty)) || 0.0;
    // Note: OL angles are in radians.
    const rotationRadians = (Math.PI * rotationDegrees) / 180.0;
    olImage.setRotation(rotationRadians);
    }

    if (symbolizer && symbolizer.graphic && symbolizer.graphic.mark && symbolizer.graphic.mark.fill && symbolizer.graphic.mark.fill.styling
        && symbolizer.graphic.mark.fill.styling.fill && symbolizer.graphic.mark.fill.styling.fill.prop) {
        let color = feature.get(symbolizer.graphic.mark.fill.styling.fill.prop);

        if (color == null || color.trim() == '') {
            color = '#0000ff'
        }
        olImage.getFill().setColor(color);
    }

  return olStyle;
}

export default getPointStyle;
